import {
  useGetAllCoursesLite,
  useGetAllMentors,
  useGetAllStudents,
  useSearchAllSessions,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import QualityScoreBadge from '@components/V4/QualityScore/QualityScoreBadge';
import RangePicker from '@components/V4/RangePicker/RangePicker';
import Select, { SelectOption } from '@components/V4/Select/Select';
import Table, { SortOrder } from '@components/V4/Table/Table';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { useAuthState } from '@contexts/AuthContext';
import { useDebounce, useQueryParamsState } from '@hooks';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import { SessionTab } from '@modules/Admin/components/DetailsModal/SessionDetailsPage/SessionTabsSection/SessionTabsSection';
import useDetailsModal from '@modules/Admin/hooks/useDetailsModal';
import { TriState, UserRole } from '@shared/common';
import {
  fiftyMinutesInSeconds,
  NO_OF_HOURS_FOR_FEEDBACK,
  SESSION_STATUSES,
  SESSION_STATUSES_OPTIONS,
  SYSTEM_MEETING_TYPE_OPTIONS,
} from '@shared/constants';
import {
  convertSecondsToTime,
  getEndDateValue,
  getStartDateValue,
  getValidMoment,
  mergeClassNames,
} from 'apps/agora/src/utils/helpers';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import OperationsExport from './OperationsExport';

const useQuery = () => new URLSearchParams(useLocation().search);

const getExtraTime = (billableTime?: number) => {
  const extraTime = Math.max((billableTime ?? 0) - fiftyMinutesInSeconds, 0);

  return extraTime === 0 ? '0m' : convertSecondsToTime(extraTime);
};

const SESSION_PROBLEM_OPTIONS: SelectOption[] = [
  { label: 'Billable time under 50 min', value: 'short_billable' },
  { label: 'Held but no billable time', value: 'no_billable' },
  { label: 'Pending review', value: 'pending_review' },
  { label: 'Unpayable', value: 'unpayable' },
  { label: 'Mentor left a comment', value: 'mentor_comment' },
];

const AllSessions = () => {
  const { roles } = useAuthState();
  const isSuperUser = roles.includes(UserRole.SuperUser);
  const [studentSearch, setStudentSearch] = useState('');
  const [mentorSearch, setMentorSearch] = useState('');

  const debouncedStudentSearch = useDebounce(studentSearch, 500);
  const debouncedMentorSearch = useDebounce(mentorSearch, 500);

  const allOption = { label: 'ALL', value: null };

  //TODO - replace this with useQueryParams when upgrading to react-router-dom v6
  const query = useQuery();
  const student = query.get('student') || null;
  const mentor = query.get('mentor') || null;
  const status = query.get('status') || null;

  const course = query.get('course') || null;
  const limit = query.get('limit') || '10';
  const page = query.get('page') || '1';
  const rangeStart = getValidMoment(query.get('rangeStart'));
  const rangeEnd = getValidMoment(query.get('rangeEnd'));
  const sortBy = query.get('sortBy') || null;
  const sortOrder = (query.get('sortOrder') || null) as SortOrder;
  const hideNoMeetingSessions = (query.get('hideNoMeetingSessions') || 'checked') as TriState;
  const systemType = query.get('systemType') || null;
  const problems = query.get('problems') || null;

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    page: page,
    limit: limit,
    student: student,
    mentor: mentor,
    rangeStart: rangeStart,
    rangeEnd: rangeEnd,
    status: status,
    course: course,
    sortBy: sortBy,
    sortOrder: sortOrder,
    hideNoMeetingSessions: hideNoMeetingSessions,
    systemType: systemType,
    problems: problems,
  });

  const { data: allSessions, isLoading: isLoadingAllMeetings } = useSearchAllSessions({
    limit: searchParams.limit,
    page: searchParams.page,
    status: searchParams.status && searchParams.status.split(','),
    course: searchParams.course,
    student: searchParams.student,
    mentor: searchParams.mentor,
    rangeStart: searchParams.rangeStart,
    rangeEnd: searchParams.rangeEnd,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
    hideNoMeetingSessions: searchParams.hideNoMeetingSessions,
    systemType: searchParams.systemType && searchParams.systemType.split(','),
    problems: searchParams.problems && searchParams.problems.split(','),
  });
  const { data: courses } = useGetAllCoursesLite();

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents({
    search: debouncedStudentSearch,
    limit: 20,
  });

  const { data: mentors, isLoading: isMentorsLoading } = useGetAllMentors({
    search: debouncedMentorSearch,
    limit: 20,
  });

  const {
    activeModalView,
    isModalOpen,
    mentorId,
    studentId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'student' });

  const rangePickerChangeHandler = (dates: [Moment | null, Moment | null]) => {
    const start = dates?.[0] ? getStartDateValue(dates?.[0]) : null;
    const end = dates?.[1] ? getEndDateValue(dates?.[1]) : null;

    setSearchParams({
      rangeStart: start ? start.toISOString() : null,
      rangeEnd: end ? end.toISOString() : null,
      page: 1,
    });
  };

  const currentPageChangeHandler = (page: number) => {
    setSearchParams({
      page,
    });
  };

  const pageSizeChangeHandler = (size: number) => {
    setSearchParams({
      limit: size,
    });
  };

  const getCourseOptions = () => {
    return (
      courses?.map((course) => ({
        label: course.title,
        value: course._id,
      })) || []
    );
  };

  const getMentorOptions = () => {
    const mentorOptions: SelectOption[] =
      mentors?.map((mentor) => ({
        label: mentor.fullName,
        value: mentor._id,
      })) || [];

    if (!debouncedMentorSearch) {
      mentorOptions.unshift(allOption);
    }

    return mentorOptions;
  };

  const getStudentOptions = () => {
    const studentOptions: SelectOption[] =
      students?.map((student) => ({
        label: student.fullName,
        value: student._id,
      })) || [];

    if (!debouncedStudentSearch) {
      studentOptions.unshift(allOption);
    }

    return studentOptions;
  };

  const getRangePickerValue = (): [Moment, Moment] | null => {
    if (!searchParams.rangeStart || !searchParams.rangeEnd) return null;

    return [moment(searchParams.rangeStart), moment(searchParams.rangeEnd)];
  };

  const sortHandler = (field: string, order: SortOrder) => {
    setSearchParams({
      sortBy: order ? field : null,
      sortOrder: order || null,
      page: 1,
    });
  };

  const getFeedbackCellClassName = (feedbackDate: Date, scheduledDate: Date, endTime?: Date) => {
    const referenceDate = endTime || scheduledDate;

    if (!feedbackDate || !referenceDate) {
      return 'text-customGrey';
    }

    const diffInHours = moment(feedbackDate).diff(moment(referenceDate), 'hours');

    return diffInHours > NO_OF_HOURS_FOR_FEEDBACK ? 'text-customRed' : 'text-customGrey';
  };

  const mentorModalOpenHandler = (mentorId: string) => {
    openModal({ page: 'mentor' }, { mentorId });
  };

  const enrollmentModalOpenHandler = (enrollmentId: string) => {
    openModal({ page: 'enrollment' }, { enrollmentId });
  };

  const sessionModalOpenHandler = (sessionId: string, tab?: SessionTab) => {
    openModal({ page: 'session', tab: tab ?? 'meetings' }, { sessionId });
  };

  const checkboxChangeHandler = (value: TriState) => {
    setSearchParams({ hideNoMeetingSessions: value, page: 1 });
  };

  const openStudentDetailsModalHandler = (studentId: string) => {
    openModal({ page: 'student' }, { studentId });
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6 bg-surfaceObject px-18 pt-8 pb-6">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-bold">All Sessions</h3>
          {isSuperUser ? <OperationsExport /> : null}
        </div>
        <div className="flex gap-6">
          <Select
            className="w-1/6 max-w-64"
            label="Course"
            value={course}
            onSelect={(value) => setSearchParams({ course: value, page: 1 })}
            options={getCourseOptions()}
            allOption={allOption}
            placeholder="Search Course"
            variant="secondary"
          ></Select>
          <Select
            className="w-1/6 max-w-64"
            label="Students"
            value={student}
            filterOption={() => true}
            searchValue={studentSearch}
            onSearch={setStudentSearch}
            onBlur={() => setStudentSearch('')}
            onSelect={(value) => setSearchParams({ student: value, page: 1 })}
            options={getStudentOptions()}
            notFoundContent={
              isStudentsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
            }
            placeholder="Search Students"
            variant="secondary"
          />
          <Select
            className="w-1/6 max-w-64"
            label="Mentor"
            value={mentor}
            filterOption={() => true}
            searchValue={mentorSearch}
            onSearch={setMentorSearch}
            onSelect={(value) => setSearchParams({ mentor: value, page: 1 })}
            onBlur={() => setMentorSearch('')}
            options={getMentorOptions()}
            notFoundContent={
              isMentorsLoading ? <LoadingSpinner className="mx-auto" size="normal" /> : null
            }
            placeholder="Search Mentor"
            variant="secondary"
          />
          <Select
            className="w-1/6 max-w-64"
            label="Status"
            value={status?.split(',').map(Number)}
            mode="multiple"
            options={SESSION_STATUSES_OPTIONS}
            onChange={(value) =>
              setSearchParams({ status: (value as string[])?.join(','), page: 1 })
            }
            placeholder="Search Status"
            variant="secondary"
            selectedOptionFirst={false}
          />
          <Select
            className="w-1/6 max-w-64"
            label="Meeting Type"
            value={systemType?.split(',')}
            mode="multiple"
            options={SYSTEM_MEETING_TYPE_OPTIONS}
            onChange={(value) =>
              setSearchParams({ systemType: (value as string[])?.join(','), page: 1 })
            }
            placeholder="Search Meeting Type"
            variant="secondary"
            selectedOptionFirst={false}
          />
          <Select
            className="w-1/6 max-w-64"
            label="Problems"
            value={problems?.split(',')}
            mode="multiple"
            options={SESSION_PROBLEM_OPTIONS}
            onChange={(value) =>
              setSearchParams({ problems: (value as string[])?.join(','), page: 1 })
            }
            placeholder="Billable time, review…"
            variant="secondary"
            selectedOptionFirst={false}
          />
          <RangePicker
            variant="secondary"
            disabled={hideNoMeetingSessions === 'unchecked'}
            label="Date Range"
            className="w-64"
            value={getRangePickerValue()}
            onChange={rangePickerChangeHandler}
          />
        </div>
        <TriStateToggle
          title="Show Sessions:"
          options={[
            { label: 'With Meetings', value: 'checked' },
            { label: 'Without Meetings', value: 'unchecked' },
            { label: 'All', value: 'indeterminate' },
          ]}
          value={hideNoMeetingSessions}
          onChange={checkboxChangeHandler}
        />
      </div>
      <div className="mb-20 mt-8">
        {isLoadingAllMeetings ? (
          <div className="h-24 flex items-center justify-center text-customGrey">Loading...</div>
        ) : (
          <Table
            className="px-18"
            pagination={{
              pageSize: limit !== null ? parseInt(limit) : 10,
              total: allSessions?.total,
              currentPage: page !== null ? parseInt(page) : 1,
              onCurrentPageChange: currentPageChangeHandler,
              onPageSizeChange: pageSizeChangeHandler,
            }}
          >
            <Table.Header>
              <Table.Row isHeader>
                <Table.HeaderCell
                  onSort={(order) => sortHandler('name', order)}
                  sortOrder={sortBy === 'name' ? sortOrder : undefined}
                >
                  Session Name
                </Table.HeaderCell>
                <Table.HeaderCell>Student</Table.HeaderCell>
                <Table.HeaderCell>Mentor</Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortHandler('scheduledDate', order)}
                  sortOrder={sortBy === 'scheduledDate' ? sortOrder : undefined}
                >
                  Date
                </Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortHandler('feedback', order)}
                  sortOrder={sortBy === 'feedback' ? sortOrder : undefined}
                >
                  Feedback
                </Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortHandler('duration', order)}
                  sortOrder={sortBy === 'duration' ? sortOrder : undefined}
                >
                  Billable Time
                </Table.HeaderCell>
                <Table.HeaderCell>Extra time (over 50)</Table.HeaderCell>
                <Table.HeaderCell>AI Score</Table.HeaderCell>
                <Table.HeaderCell
                  onSort={(order) => sortHandler('status', order)}
                  sortOrder={sortBy === 'status' ? sortOrder : undefined}
                >
                  Status
                </Table.HeaderCell>
                <Table.HeaderCell>Go To</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {allSessions?.sessions?.map((session) => (
                <Table.Row key={session._id}>
                  <Table.Cell onClick={() => sessionModalOpenHandler(session._id)}>
                    {session.name ?? 'n/a'}
                  </Table.Cell>
                  <Table.Cell
                    onClick={() => openStudentDetailsModalHandler(session?.students[0]?._id)}
                  >
                    {session?.students?.[0]?.fullName ?? 'n/a'}
                  </Table.Cell>
                  <Table.Cell
                    onClick={session.mentor && (() => mentorModalOpenHandler(session.mentor._id))}
                  >
                    {session.mentor?.fullName ?? 'n/a'}
                  </Table.Cell>
                  <Table.Cell>
                    {session.scheduledDate
                      ? moment(session.scheduledDate).format('DD MMM YYYY, HH:mm:SS')
                      : 'n/a'}
                  </Table.Cell>
                  <Table.Cell
                    onClick={() => sessionModalOpenHandler(session._id, 'feedback')}
                    className={getFeedbackCellClassName(
                      session.feedbackDate,
                      moment(session.scheduledDate).toDate(),
                      session.endTime
                    )}
                  >
                    {session.feedbackDate
                      ? moment(session.feedbackDate).format('DD MMM YYYY, HH:mm')
                      : 'n/a'}
                  </Table.Cell>
                  <Table.Cell
                    className={mergeClassNames({
                      'text-customRed':
                        typeof session?.billableTime === 'number' &&
                        session.billableTime < fiftyMinutesInSeconds,
                    })}
                  >
                    {session?.billableTime === 0
                      ? '0m'
                      : convertSecondsToTime(session?.billableTime)}
                    {session?.billableTimeReason ? (
                      <span
                        className="ml-1 cursor-help text-customGold"
                        title={`Mentor: ${session.billableTimeReason}`}
                      >
                        ●
                      </span>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>{getExtraTime(session?.billableTime)}</Table.Cell>
                  <Table.Cell>
                    <QualityScoreBadge
                      score={session.qualityScore}
                      onClick={() => sessionModalOpenHandler(session._id, 'ai-scoring')}
                    />
                  </Table.Cell>
                  <Table.Cell>{SESSION_STATUSES[session.status]}</Table.Cell>
                  <Table.Cell onClick={() => enrollmentModalOpenHandler(session.enrollmentId)}>
                    Enrollment
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default AllSessions;
